import React from 'react';
import { COLORS, Panel, Table } from '../ui';
import { ColumnChart } from '../charts';

/**
 * Screen 2 -- coordinators.
 *
 * The scorecard deliberately shows volume and speed side by side. Issued on its
 * own rewards whoever clears the queue fastest regardless of what they send
 * out, so "% within 1 business day" and "follow-up rate" sit next to it: a high
 * follow-up rate against high volume means the first issue was not right.
 */

const pctText = v => v == null ? '—' : `${Math.round(v * 100)}%`;
const daysText = v => v == null ? '—' : `${v.toFixed(1)}d`;

/** Green when the target is met, red when missed, grey where there is no target. */
function Pct({ value, target, invert }) {
    if (value == null) return <span style={{ color: COLORS.muted }}>—</span>;
    let color = COLORS.ink;
    if (target != null) {
        const met = invert ? value <= target : value >= target;
        color = met ? 'var(--ui-tone-good-fg, #046804)' : COLORS.critical;
    }
    return <span style={{ color, fontWeight: 600 }}>{pctText(value)}</span>;
}

export default function PeopleScreen({ data, onOpenList, onPickPerson }) {
    const { scorecard, total, dwellHistogram, trend } = data;

    const columns = [
        {
            key: 'person', label: 'Person',
            render: r => <span style={{ fontWeight: 600, color: COLORS.link, cursor: 'pointer' }}>{r.person}</span>,
        },
        { key: 'issued', label: 'Issued', align: 'right' },
        {
            key: 'within1BdPct', label: '% within 1 BD', align: 'right',
            render: r => <Pct value={r.within1BdPct} target={0.9} />,
        },
        {
            key: 'medianDwellBusinessDays', label: 'Median dwell', align: 'right',
            render: r => daysText(r.medianDwellBusinessDays),
        },
        {
            key: 'waitingNow', label: 'Waiting now', align: 'right',
            render: r => <span style={{ fontWeight: 700, color: r.waitingNow > 0 ? COLORS.critical : COLORS.ink }}>
                {r.waitingNow}
            </span>,
        },
        {
            key: 'stalePct', label: 'Stale %', align: 'right',
            render: r => <Pct value={r.stalePct} target={0.1} invert />,
        },
        { key: 'closed', label: 'Closed', align: 'right' },
        {
            key: 'followupPct', label: 'Follow-up %', align: 'right',
            render: r => <Pct value={r.followupPct} target={0.1} invert />,
        },
    ];

    // The team total belongs in the table, not beside it, so every column can be
    // read against the team's own figure.
    const rows = [...scorecard, { ...total, __total: true }];

    const histogram = dwellHistogram.map(b => ({
        label: b.label,
        value: b.count,
        color: b.overTarget ? COLORS.serious : COLORS.good,
    }));

    const trendPoints = trend
        .filter(d => d.issued > 0)
        .map(d => ({ label: d.date.slice(5), value: Math.round((d.within1BdPct || 0) * 100) }));

    return (
        <>
            <Panel
                title="Coordinator scorecard"
                subtitle="Sorted by what is waiting with them now. Click a row for that person's work orders."
                right={<span style={{ fontSize: 11.5, color: COLORS.muted }}>
                    Issued-by comes from MC's status history, not Taken By
                </span>}
            >
                <Table
                    columns={columns}
                    rows={rows}
                    rowKey={r => r.person}
                    maxHeight={480}
                    onRowClick={r => { if (!r.__total) onPickPerson(r.person); }}
                    empty="Nobody issued or closed work in this period."
                />
            </Panel>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 16 }}>
                <Panel
                    title="Dwell distribution"
                    subtitle="Work orders issued in the period, by business hours from request to issue"
                >
                    <ColumnChart data={histogram} height={170} />
                    <div style={{ fontSize: 11.5, color: COLORS.muted, marginTop: 8 }}>
                        Green is inside the one business day target; orange is past it.
                    </div>
                </Panel>

                <Panel title="% issued within 1 business day" subtitle="Per day across the period, team-wide">
                    {trendPoints.length > 0
                        ? <ColumnChart data={trendPoints} height={170} color={COLORS.series[0]} />
                        : <div style={{ color: COLORS.muted, fontSize: 13 }}>Nothing was issued in this period.</div>}
                </Panel>
            </div>

            <div style={{ fontSize: 11.5, color: COLORS.muted, marginTop: -6 }}>
                Waiting now is attributed by Taken By, because work that has not been issued has no issuer yet.
                Everything else is attributed to the person who actually made the change.
                <button
                    onClick={() => onOpenList('waiting', 'Waiting to be issued')}
                    style={{ background: 'none', border: 'none', color: COLORS.link, cursor: 'pointer', fontSize: 11.5, padding: '0 4px' }}
                >See everything waiting</button>
            </div>
        </>
    );
}
